/**
 * Utility functions for persisting dashboard filter state to localStorage
 * This allows users to maintain their filter settings across page refreshes
 */

import { DEFAULT_INDEX_FILTER } from '../constants';

const FILTER_STORAGE_KEY = 'dashboard_filters_v1';

/**
 * Save dashboard filter state to localStorage
 * @param {Object} filterState - Complete filter state object to save
 */
export const saveFiltersToStorage = (filterState) => {
  try {
    const serializedState = JSON.stringify(filterState);
    localStorage.setItem(FILTER_STORAGE_KEY, serializedState);
  } catch (error) {
    console.warn('Failed to save filters to localStorage:', error);
  }
};

/**
 * Load dashboard filter state from localStorage
 * @param {Object} defaultFilters - Default filter values to use as fallback
 * @returns {Object} Restored filter state or default filters if none saved
 */
export const loadFiltersFromStorage = (defaultFilters) => {
  try {
    const serializedState = localStorage.getItem(FILTER_STORAGE_KEY);
    if (serializedState === null) {
      return defaultFilters;
    }
    
    const savedState = JSON.parse(serializedState);
    
    // Merge with defaults to ensure all required properties exist
    // This handles cases where new filters are added in future versions
    const merged = {
      ...defaultFilters,
      ...savedState,
    };
    if (!Array.isArray(merged.index) || merged.index.length === 0) {
      merged.index = [...(defaultFilters.index?.length ? defaultFilters.index : DEFAULT_INDEX_FILTER)];
    }
    return merged;
  } catch (error) {
    console.warn('Failed to load filters from localStorage:', error);
    return defaultFilters;
  }
};

/**
 * Clear saved filter state from localStorage
 */
export const clearSavedFilters = () => {
  try {
    localStorage.removeItem(FILTER_STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to clear saved filters:', error);
  }
};

/**
 * Get a summary of which filters have non-default values
 * @param {Object} filterState - Current filter state
 * @param {Object} defaultFilters - Default filter values
 * @returns {string} Human readable summary of active filters
 */
export const getActiveFiltersSummary = (filterState, defaultFilters) => {
  const activeFilters = [];
  
  // Check each filter for non-default values
  Object.keys(filterState).forEach(key => {
    const value = filterState[key];
    const defaultValue = defaultFilters[key];
    
    if (key === 'strategy' || key === 'pattern' || key === 'index' || key === 'signalType' || key === 'stockSymbols') {
      if (Array.isArray(value) && value.length > 0) {
        activeFilters.push(key);
      }
    } else if (key === 'confidence') {
      if (Array.isArray(value) && (value[0] !== 0 || value[1] !== 100)) {
        activeFilters.push('confidence');
      }
    } else if (key === 'instrumentType') {
      if (value !== 'all') {
        activeFilters.push('instrument');
      }
    } else if (key === 'days') {
      if (value !== 30) {
        activeFilters.push('timeRange');
      }
    } else if (value !== defaultValue) {
      activeFilters.push(key);
    }
  });
  
  return activeFilters.length > 0 ? `Active: ${activeFilters.join(', ')}` : 'No active filters';
};

/**
 * Check if there are saved filters in localStorage
 * @returns {boolean} True if saved filters exist
 */
export const hasSavedFilters = () => {
  try {
    return localStorage.getItem(FILTER_STORAGE_KEY) !== null;
  } catch (error) {
    return false;
  }
};